import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import toastSlice from './slices/toastSlice';
import displaySettingsSlice from './slices/displaySettingsSlice';
import { technicalSupportApi } from './api/technicalSupportApi';
import contentContextSlice from './slices/contentContextSlice';
import contentSlice from './slices/contentSlice';
import sessionSlice from './slices/sessionSlice';
import { contentContextApi } from './api/contentContextApi';
import { contentApi } from './api/contentApi';
import { flowApi } from './api/flowApi';
import { mediaApi } from './api/mediaApi';
import modalMediaSlice from './slices/modalMediaSlice';
import { notificationConfigurationsApi } from './api/notificationConfigurationApi';
import { userApi } from './api/UserListApi';
import toastMiddleware from './middlewares/toastMiddleware/toastMiddleware';
import { versionManagmentApi } from './api/versionManagment';
import { statisticsApi } from './api/statisticsApi';
import { questionnaireApi } from './api/questionnaireApi';
import { broadcastApi } from './api/broadcastApi';
import { sessionsApi } from './api/sessionsApi';
import { istiftaApi } from './api/istiftaApi';
import { istiftaLinksApi } from './api/istiftaLinksApi';

// Every RTK Query API slice. Signing out resets each one so no cached data outlives the session.
export const apiSlices = [
  contentContextApi,
  contentApi,
  flowApi,
  mediaApi,
  notificationConfigurationsApi,
  userApi,
  versionManagmentApi,
  technicalSupportApi,
  statisticsApi,
  questionnaireApi,
  broadcastApi,
  sessionsApi,
  istiftaApi,
  istiftaLinksApi,
];

const persistConfig = {
  key: 'root',
  storage,
  blacklist: [
    'toast',
    'session',
    technicalSupportApi.reducerPath,
    statisticsApi.reducerPath,
    istiftaApi.reducerPath,
    istiftaLinksApi.reducerPath,
  ],
};

const rootReducer = combineReducers({
  toast: toastSlice,
  displaySettings: displaySettingsSlice,
  contentContext: contentContextSlice,
  content: contentSlice,
  session: sessionSlice,
  modal: modalMediaSlice,
  [contentContextApi.reducerPath]: contentContextApi.reducer,
  [contentApi.reducerPath]: contentApi.reducer,
  [flowApi.reducerPath]: flowApi.reducer,
  [mediaApi.reducerPath]: mediaApi.reducer,
  [notificationConfigurationsApi.reducerPath]:
    notificationConfigurationsApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [versionManagmentApi.reducerPath]: versionManagmentApi.reducer,
  [technicalSupportApi.reducerPath]: technicalSupportApi.reducer,
  [statisticsApi.reducerPath]: statisticsApi.reducer,
  [questionnaireApi.reducerPath]: questionnaireApi.reducer,
  [broadcastApi.reducerPath]: broadcastApi.reducer,
  [sessionsApi.reducerPath]: sessionsApi.reducer,
  [istiftaApi.reducerPath]: istiftaApi.reducer,
  [istiftaLinksApi.reducerPath]: istiftaLinksApi.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        ignoredPaths: ['register'],
      },
      immutableCheck: false,
    })
      .concat(contentContextApi.middleware)
      .concat(contentApi.middleware)
      .concat(flowApi.middleware)
      .concat(mediaApi.middleware)
      .concat(notificationConfigurationsApi.middleware)
      .concat(userApi.middleware)
      .concat(versionManagmentApi.middleware)
      .concat(toastMiddleware)
      .concat(technicalSupportApi.middleware)
      .concat(statisticsApi.middleware)
      .concat(questionnaireApi.middleware)
      .concat(broadcastApi.middleware)
      .concat(sessionsApi.middleware)
      .concat(istiftaApi.middleware)
      .concat(istiftaLinksApi.middleware),
});

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
